import type { MarketingTranslator } from '@/i18n/marketing'
import { useTranslation } from 'react-i18next'
import { MarqueeRow } from '@marketing/components'

type PatentCountry = 'korea' | 'china' | 'US'

interface PatentItem {
  imgId: string
  country: PatentCountry
  title: string
}

interface TechnologySectionProps {
  title: string
  titleHighlight: string
  description: string
  patentNotice: string
  patents: PatentItem[]
}

const S3 = 'https://d2ql3aoebfyj3b.cloudfront.net'
const FLAG_URL: Record<PatentCountry, string> = {
  korea: `${S3}/event/summer-promotion-2507/patent-section/04-img-icon-korea.svg`,
  china: `${S3}/event/summer-promotion-2507/patent-section/04-img-icon-china.svg`,
  US: `${S3}/event/summer-promotion-2507/patent-section/04-img-icon-US.svg`,
}
function getCountryNames(mt: MarketingTranslator) {
  const COUNTRY_NAME: Record<PatentCountry, string> = {
    korea: mt('patentOffice.korea'),
    china: mt('patentOffice.china'),
    US: mt('patentOffice.us'),
  }
  return COUNTRY_NAME
}

const LAUREL_LEFT = `${S3}/quebon-introduction/section06/img-laurel-left.svg`
const LAUREL_RIGHT = `${S3}/quebon-introduction/section06/img-laurel-right.svg`

const PATENT_IMG_SHADOW =
  '0px 38.519px 25.481px 0px rgba(0,0,0,0.04), 0px 20px 13px 0px rgba(0,0,0,0.04), 0px 8.148px 6.519px 0px rgba(0,0,0,0.03), 0px 1.852px 3.148px 0px rgba(0,0,0,0.02)'

function PatentCard({ item }: { item: PatentItem }) {
  const { t: mt } = useTranslation('marketing')
  const COUNTRY_NAME = getCountryNames(mt)
  return (
    <div className="w-[224px] flex flex-col px-6 shrink-0">
      <img
        src={`${S3}/event/summer-promotion-2507/patent-section/04-img-patent${item.imgId}@2x.png`}
        alt={item.title}
        className="w-full rounded-lg block"
        style={{ boxShadow: PATENT_IMG_SHADOW }}
      />
      <div className="mt-4 flex flex-col gap-1">
        <img
          src={FLAG_URL[item.country]}
          alt={item.country}
          className="w-6 h-6"
        />
        <div className="flex flex-col gap-1">
          <p className="text-[12px] leading-[1.4] text-[#333] tracking-[-0.6px]">
            {COUNTRY_NAME[item.country]}
          </p>
          <p className="text-[14px] leading-[1.4] font-semibold text-qb-text-high tracking-[-0.6px]">
            {item.title}
          </p>
        </div>
      </div>
    </div>
  )
}

export function TechnologySection({
  title,
  titleHighlight,
  description,
  patentNotice,
  patents,
}: TechnologySectionProps) {

  return (
    <section id="technology" data-marketing-section="" className="w-full bg-qb-blue-50 flex flex-col items-center gap-14 lg:gap-[104px] py-14 lg:py-[104px]">
      {/* Header content */}
      <div className="w-full max-w-[800px] flex flex-col items-center gap-8">
        <div className="flex flex-col items-center gap-2 lg:gap-4 px-6 lg:px-0">
          <h2 className="font-bold text-[28px] lg:text-[40px] leading-[1.2] tracking-[-0.6px] text-qb-text-high text-center">
            {title}
          </h2>
          <h2 className="font-bold text-[28px] lg:text-[40px] leading-[1.2] tracking-[-0.6px] text-qb-primary text-center">
            {titleHighlight}
          </h2>
        </div>

        <p className="font-normal text-[18px] lg:text-[20px] leading-[1.5] tracking-[-0.6px] text-qb-text-high text-center whitespace-pre-line px-6 lg:px-0">
          {description}
        </p>

        {/* Achievements: laurel + flags + notice + laurel */}
        <div className="flex justify-center items-center gap-4 pt-2">
          <img
            src={LAUREL_LEFT}
            alt=""
            aria-hidden="true"
            className="w-[25px] h-[45px]"
          />
          <div className="flex flex-col items-center gap-3">
            <div className="flex gap-2">
              {(['korea', 'china', 'US'] as const).map((c) => (
                <div
                  key={c}
                  className="w-6 h-6 rounded-full bg-white shadow-[0px_2px_4px_0px_rgba(0,0,0,0.2)] flex items-center justify-center"
                >
                  <img src={FLAG_URL[c]} alt={c} className="w-5 h-5" />
                </div>
              ))}
            </div>
            <p className="font-normal text-[14px] leading-[1.5] tracking-[-0.6px] text-qb-text-mid text-center">
              {patentNotice}
            </p>
          </div>
          <img
            src={LAUREL_RIGHT}
            alt=""
            aria-hidden="true"
            className="w-[25px] h-[45px]"
          />
        </div>
      </div>

      {/* Patent marquee — full width */}
      <div className="w-full">
        <MarqueeRow speed="slow" gap={0}>
          {patents.map((p) => (
            <PatentCard key={p.imgId} item={p} />
          ))}
        </MarqueeRow>
      </div>
    </section>
  )
}
